import React, { useEffect, useRef, useState } from 'react';

import { getADBanners } from '@api/FW/ADbanner/banner';
import { apiErrorHandler } from '@app/services/error.service';
import { StyledBannerWrapper } from '@components/shared/ADBannerCarousel/ADBannerCarousel.style';
import { IADBanner } from '@components/shared/ADBanner/types';
import { useResponsive } from '@hooks/shared/useResponsive';
import { Carousel, Skeleton } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

const AUTOPLAY_SPEED = 5000;
/** Pointer travel past this (px) counts as a drag rather than a tap. */
const DRAG_THRESHOLD = 8;

const ADBannerCarousel = () => {
	const { mobileOnly, desktopOnly, isTablet, isSmallDesktop, isDesktop } = useResponsive();

	const [isLoading, setIsLoading] = useState(true);
	const [banners, setBanners] = useState<IADBanner[]>([]);

	const isXLarge = useMediaQuery({ minWidth: 1536 });
	const isMobile = useMediaQuery({ minWidth: 768 });

	const bannerHeight = isXLarge
		? 314
		: desktopOnly
			? 261
			: isSmallDesktop
				? 200
				: isTablet
					? 154
					: isMobile
						? 252
						: 'auto';

	useEffect(() => {
		getADBanners()
			.then((loadData) => setBanners(loadData))
			.catch((error) => apiErrorHandler(error))
			.finally(() => setIsLoading(false));
	}, []);

	// A lone banner gets no autoplay and no dots — rotating a single slide is pointless motion.
	const hasMultiple = banners.length > 1;
	// Desktop navigates with the arrows and dots; drag/swipe stays for touch-sized viewports.
	const allowSwipe = !isDesktop;

	const pointerStartX = useRef<number | null>(null);
	const isDragging = useRef(false);

	const handlePointerDown = (event: React.PointerEvent<HTMLAnchorElement>) => {
		pointerStartX.current = event.clientX;
		isDragging.current = false;
	};

	const handlePointerMove = (event: React.PointerEvent<HTMLAnchorElement>) => {
		if (pointerStartX.current === null) return;
		if (Math.abs(event.clientX - pointerStartX.current) > DRAG_THRESHOLD) isDragging.current = true;
	};

	// A swipe finishes as a click on the slide's anchor, so dragging would otherwise follow
	// the banner's link. Swallow that click and let a genuine tap through.
	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		if (isDragging.current) event.preventDefault();
		isDragging.current = false;
		pointerStartX.current = null;
	};

	const dragGuardProps = {
		onPointerDown: handlePointerDown,
		onPointerMove: handlePointerMove,
		onClick: handleClick,
	};

	const renderBanner = (banner: IADBanner) => {
		const image = (
			<img
				className='h-full'
				src={mobileOnly ? banner.Mobile : banner.Desktop}
				style={{ position: 'relative', left: 'unset', width: '100%', height: 'auto' }}
				alt='homepage banner'
			/>
		);

		return banner.External ? (
			<a
				href={banner.Navigate || '#'}
				className='hover-opacity'
				target='_blank'
				rel='noopener noreferrer'
				{...dragGuardProps}>
				{image}
			</a>
		) : (
			<Link to={banner.Navigate || ''} className='hover-opacity' {...dragGuardProps}>
				{image}
			</Link>
		);
	};

	// The wrapper carries an explicit height, so rendering it with nothing to show would
	// reserve a banner-sized gap on the page.
	if (!isLoading && !banners.length) return null;

	return (
		<StyledBannerWrapper className='banner-wrapper' style={{ height: bannerHeight }}>
			{isLoading && (
				<Skeleton.Button style={{ height: bannerHeight, width: '100%', position: 'absolute' }} />
			)}

			{!banners.length ? null : (
				<Carousel
					autoplay={hasMultiple}
					autoplaySpeed={AUTOPLAY_SPEED}
					dots={hasMultiple}
					arrows={hasMultiple}
					infinite={hasMultiple}
					draggable={allowSwipe}
					swipe={allowSwipe}>
					{banners.map((banner, index) => (
						<div key={`${banner.Desktop}-${index}`}>{renderBanner(banner)}</div>
					))}
				</Carousel>
			)}
		</StyledBannerWrapper>
	);
};

export default ADBannerCarousel;
